import { locationStateKeyedByPath } from "@/constants/paths";
import { useMatchPath } from "@/lib/react-router";
import { CurrencyCode } from "@ternary/api-lib/constants/enums";
import { StringParam, useQueryParams } from "use-query-params";
import useAuthenticatedUser from "./useAuthenticatedUser";
import useGatekeeper from "./useGatekeeper";

/**
 * Returns the effective currency code for the current user and route context.
 * Handles multi-currency feature gating and path-based enablement.
 * Returns `undefined` if multi-currency is not enabled or not supported for the current path.
 */
export default function usePreferredCurrency(): CurrencyCode | undefined {
  const gatekeeper = useGatekeeper();
  const authenticatedUser = useAuthenticatedUser();
  const currentPath = useMatchPath();
  const [searchParamState] = useQueryParams({
    tab: StringParam,
  });

  const locationState = locationStateKeyedByPath[currentPath];
  const multiCurrencyDisabled = locationState?.multiCurrencyDisabled;

  if (!gatekeeper.canAccessMultiCurrency) {
    return undefined;
  }

  const isMultiCurrencyEnabled = (): boolean => {
    if (typeof multiCurrencyDisabled === "boolean") {
      return !multiCurrencyDisabled;
    }

    if (multiCurrencyDisabled === "partial") {
      return true;
    }

    if (multiCurrencyDisabled?.disabledForTabs && searchParamState.tab) {
      return !multiCurrencyDisabled.disabledForTabs.includes(
        searchParamState.tab
      );
    }

    return true;
  };

  return isMultiCurrencyEnabled()
    ? authenticatedUser.tenant.preferredCurrencyCode
    : CurrencyCode.USD;
}
